// ---------------------------------------------------------------------------
// G.19: Breakout Rooms — Host Setup Panel
// ---------------------------------------------------------------------------

import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { AssignmentMode, RoomDefinition, CreateBreakoutResponse } from "./breakout-types";
import { MAX_ROOMS, MIN_ROOMS, DEFAULT_TIMER_MINUTES, DEFAULT_WARNING_MINUTES } from "./breakout-types";

interface BreakoutSetupPanelProps {
  webinarId: string;
  onCreated: (breakoutId: string) => void;
}

export function BreakoutSetupPanel({ webinarId, onCreated }: BreakoutSetupPanelProps) {
  const [rooms, setRooms] = useState<RoomDefinition[]>([
    { name: "Room 1", topic: "", maxParticipants: null },
    { name: "Room 2", topic: "", maxParticipants: null },
  ]);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>("random");
  const [timerMinutes, setTimerMinutes] = useState(DEFAULT_TIMER_MINUTES);
  const [warningMinutesBefore, setWarningMinutesBefore] = useState(DEFAULT_WARNING_MINUTES);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addRoom = () => {
    if (rooms.length >= MAX_ROOMS) return;
    setRooms([...rooms, { name: `Room ${rooms.length + 1}`, topic: "", maxParticipants: null }]);
  };

  const removeRoom = (index: number) => {
    if (rooms.length <= MIN_ROOMS) return;
    setRooms(rooms.filter((_, i) => i !== index));
  };

  const updateRoom = (index: number, field: keyof RoomDefinition, value: string | number | null) => {
    setRooms(rooms.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const res = await apiFetch<CreateBreakoutResponse>(
        `/v1/webinars/${webinarId}/breakout/create`,
        {
          method: "POST",
          body: JSON.stringify({
            rooms: rooms.map((r) => ({
              name: r.name,
              topic: r.topic || undefined,
              maxParticipants: r.maxParticipants || undefined,
            })),
            assignmentMode,
            timerMinutes,
            warningMinutesBefore,
          }),
        },
      );
      onCreated(res.breakoutId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create breakout");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h2 className="mb-4 text-base font-semibold text-foreground">Create Breakout Rooms</h2>

      {/* Room List */}
      <div className="mb-4 space-y-3">
        {rooms.map((room, idx) => (
          <div key={idx} className="flex items-start gap-2 rounded-md border border-border bg-background p-3">
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={room.name}
                onChange={(e) => updateRoom(idx, "name", e.target.value)}
                placeholder="Room name"
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
                maxLength={100}
              />
              <input
                type="text"
                value={room.topic}
                onChange={(e) => updateRoom(idx, "topic", e.target.value)}
                placeholder="Discussion topic (optional)"
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
                maxLength={500}
              />
            </div>
            {rooms.length > MIN_ROOMS && (
              <button
                onClick={() => removeRoom(idx)}
                className="mt-1 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={`Remove ${room.name}`}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {rooms.length < MAX_ROOMS && (
        <button
          onClick={addRoom}
          className="mb-4 w-full rounded-md border border-dashed border-border py-2 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
        >
          + Add Room ({rooms.length}/{MAX_ROOMS})
        </button>
      )}

      {/* Assignment Mode */}
      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-medium text-foreground">Assignment Mode</label>
        <div className="flex gap-2">
          {(["random", "manual", "self_select"] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setAssignmentMode(mode)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                assignmentMode === mode
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-foreground hover:bg-accent"
              }`}
            >
              {mode === "self_select" ? "Self-Select" : mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {assignmentMode === "random" && "Participants will be evenly distributed across rooms."}
          {assignmentMode === "manual" && "You assign participants to specific rooms before or during the session."}
          {assignmentMode === "self_select" && "Participants choose which room to join."}
        </p>
      </div>

      {/* Timer */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Duration (minutes)</label>
          <input
            type="number"
            value={timerMinutes}
            onChange={(e) => setTimerMinutes(Math.max(1, Math.min(120, Number(e.target.value))))}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            min={1}
            max={120}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Warning before end (min)</label>
          <input
            type="number"
            value={warningMinutesBefore}
            onChange={(e) => setWarningMinutesBefore(Math.max(1, Math.min(10, Number(e.target.value))))}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            min={1}
            max={10}
          />
        </div>
      </div>

      {error && (
        <p className="mb-3 text-sm text-[var(--color-destructive)]">{error}</p>
      )}

      <button
        onClick={handleCreate}
        disabled={isCreating || rooms.some((r) => !r.name.trim())}
        className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {isCreating ? "Creating..." : "Create Breakout Rooms"}
      </button>
    </div>
  );
}
